import { ChevronLeftIcon } from '@approvalmax/ui';
import { Box, Button, Grid, Popup, Text, toast } from '@approvalmax/ui/src/components';
import { intl, mathService } from '@approvalmax/utils';
import { useSetAtom } from 'jotai';
import { FC, useCallback, useMemo } from 'react';
import { useCreateConversion } from 'shared/data/wallets/v1';
import { useCompanyIdBasedOnUrl } from 'shared/hooks';

import { convertCurrencyPopupState } from '../ConvertCurrencyPopup/ConvertCurrencyPopup.states';
import { messages } from './ConfirmConvertCurrencyPopupContent.messages';
import { ConfirmConvertCurrencyPopupContentProps } from './ConfirmConvertCurrencyPopupContent.types';

export const ConfirmConvertCurrencyPopupContent: FC<ConfirmConvertCurrencyPopupContentProps> = (props) => {
    const { quote, quoteDate, onBack } = props;

    const { companyId } = useCompanyIdBasedOnUrl();
    const setConvertCurrencyPopupState = useSetAtom(convertCurrencyPopupState);
    const { mutate, isLoading } = useCreateConversion({
        onSuccess: () => {
            setConvertCurrencyPopupState({
                isOpen: false,
            });

            toast.success(messages.success);
        },
    });

    const rate = useMemo(() => {
        return mathService.round(mathService.divide(1, quote.clientRate), 6);
    }, [quote.clientRate]);

    const handleBack = useCallback(() => {
        setConvertCurrencyPopupState({
            isOpen: true,
            fromDefaultCurrency: quote.sellCurrency,
            toDefaultCurrency: quote.buyCurrency,
        });

        onBack();
    }, [setConvertCurrencyPopupState, quote, onBack]);

    const handleSubmit = useCallback(() => {
        const now = Date.now();

        const { id: quoteId, ...restQuote } = quote;

        mutate({
            params: {
                path: {
                    organizationId: companyId,
                },
            },
            body: {
                idempotencyId: `${quoteId}-${now}`,
                conversionQuoteId: quoteId,
                isApproved: true,
                ...restQuote,
            },
        });
    }, [mutate, quote, companyId]);

    return (
        <>
            <Popup.Header title={messages.review} />

            <Popup.Body>
                <Grid rowGap={16}>
                    <Text color='midnight80' font='body' fontSize='medium'>
                        {messages.checkAndConfirm}
                    </Text>

                    <Grid gridTemplateColumns='1fr 1fr' gap={12}>
                        <Box color='midnight20' radius='small' spacing='12'>
                            <Grid rowGap={8}>
                                <Text font='label' fontSize='medium'>
                                    {messages.from}
                                </Text>

                                <Grid gridTemplateColumns='max-content max-content' gap={8}>
                                    <Text color='midnight70' font='label' fontSize='medium'>
                                        {messages.wallet}
                                    </Text>

                                    <Text font='label' fontSize='medium'>
                                        {quote.sellCurrency}
                                    </Text>
                                </Grid>

                                <Grid gridTemplateColumns='max-content max-content' gap={8}>
                                    <Text color='midnight70' font='label' fontSize='medium'>
                                        {messages.sell}
                                    </Text>

                                    <Text font='label' fontSize='medium'>
                                        {intl.formatCurrency(quote.clientSellAmount, quote.sellCurrency)}
                                    </Text>
                                </Grid>
                            </Grid>
                        </Box>

                        <Box color='midnight20' radius='small' spacing='12'>
                            <Grid rowGap={8}>
                                <Text font='label' fontSize='medium'>
                                    {messages.to}
                                </Text>

                                <Grid gridTemplateColumns='max-content max-content' gap={8}>
                                    <Text color='midnight70' font='label' fontSize='medium'>
                                        {messages.wallet}
                                    </Text>

                                    <Text font='label' fontSize='medium'>
                                        {quote.buyCurrency}
                                    </Text>
                                </Grid>

                                <Grid gridTemplateColumns='max-content max-content' gap={8}>
                                    <Text color='midnight70' font='label' fontSize='medium'>
                                        {messages.buy}
                                    </Text>

                                    <Text font='label' fontSize='medium'>
                                        {intl.formatCurrency(quote.clientBuyAmount, quote.buyCurrency)}
                                    </Text>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>

                    <Grid rowGap={8}>
                        <Grid gridTemplateColumns='max-content max-content' gap={8}>
                            <Text color='midnight70' font='label' fontSize='medium'>
                                {messages.exchangeRate}
                            </Text>

                            <Text font='label' fontSize='medium'>
                                {messages.exchangeRateValue({
                                    fromCurrency: quote.sellCurrency,
                                    toCurrency: quote.buyCurrency,
                                    rate: intl.formatNumber(rate, 'inherit'),
                                })}
                            </Text>
                        </Grid>

                        <Grid gridTemplateColumns='max-content max-content' gap={8}>
                            <Text color='midnight70' font='label' fontSize='medium'>
                                {messages.date}
                            </Text>

                            <Text font='label' fontSize='medium'>
                                {quoteDate}
                            </Text>
                        </Grid>
                    </Grid>

                    <Text color='midnight70' font='label' fontSize='medium'>
                        {messages.note}
                    </Text>

                    <Grid gridTemplateColumns='max-content max-content' gap={16} justifyContent='flex-end'>
                        <Button
                            color='midnight40'
                            variant='outline'
                            startIcon={<ChevronLeftIcon color='midnight100' />}
                            onClick={handleBack}
                            disabled={isLoading}
                        >
                            {messages.back}
                        </Button>

                        <Button color='blue80' progress={isLoading} onClick={handleSubmit}>
                            {messages.confirm}
                        </Button>
                    </Grid>
                </Grid>
            </Popup.Body>
        </>
    );
};

ConfirmConvertCurrencyPopupContent.displayName = 'ConfirmConvertCurrencyPopupContent';
